import { initializeApp } from 'firebase/app'
import { getMessaging, getToken, onMessage } from 'firebase/messaging'
import { store } from './redux/store'
import { toggleTriggered } from './redux/reducers/pushSlice'

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
}

export const app = initializeApp(firebaseConfig)

// Electron has no Web Push service, so there FCM is handled in the main process
// (electron-push-receiver) and exposed on window.electronPush by public/preload.js.
export const isElectronPush = typeof window !== 'undefined' && !!window.electronPush

let messaging = null
if (!isElectronPush) {
  try {
    messaging = getMessaging(app)
  } catch (err) {
    console.error('Firebase messaging unavailable:', err)
  }
}
export { messaging }

/**
 * Registers for push and resolves with the FCM token to send to the backend.
 * Rejects if push is not available.
 */
export const requestPushToken = async (vapidKey) => {
  if (isElectronPush) {
    return new Promise((resolve, reject) => {
      const unsubs = []
      const done = (fn) => (value) => {
        unsubs.forEach((u) => u())
        fn(value)
      }
      unsubs.push(window.electronPush.onStarted(done(resolve)))
      unsubs.push(window.electronPush.onTokenUpdated(done(resolve)))
      unsubs.push(window.electronPush.onError(done((msg) => reject(new Error(msg)))))
      window.electronPush.start({
        firebase: {
          apiKey: firebaseConfig.apiKey,
          appID: firebaseConfig.appId,
          projectID: firebaseConfig.projectId,
        },
        vapidKey,
      })
    })
  }

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') throw new Error(`Notification permission ${permission}`)
  if (!messaging) throw new Error('Firebase messaging unavailable')
  return getToken(messaging, { vapidKey })
}

/**
 * Subscribes to foreground push messages. The payload has the same shape in both
 * environments ({ notification: { title, body }, data: { payload, message } }).
 * Returns an unsubscribe function.
 */
export const subscribeToPushMessages = (callback) => {
  const handler = (payload) => {
    store.dispatch(toggleTriggered())
    callback(payload)
  }
  if (isElectronPush) return window.electronPush.onNotification(handler)
  if (!messaging) return () => {}
  return onMessage(messaging, handler)
}
